import { Empty, FilterIcon, HeaderWithSort, Table, Tag } from 'fave-ui';
import { PageHeaderWithSpace } from '../../../components/common';
import { ColumnsType, ColumnType } from 'antd/lib/table';
import {
  calcTableHeight,
  calcTableWidth,
  createOnTableChangeHandler,
  dateOrDash,
  formatFilter,
  valueOrDash,
} from '../../../utils/utilFunctions';
import { useFetchMerchants } from '../../../services/merchants';
import { tableConfigDefault } from '../../ProductDashboardView/DealsTab/DealsTab';
import { useState } from 'react';
import { TableSettingsType } from '../../../types/configTypes';
import { CompanyType } from '../../../types/dataTypes';
import style from './style.module.css';
import { SortOrder } from 'antd/lib/table/interface';
import { RenderColumn } from '../../../helpers/tableHelpers/RenderColumn';
import useCleverTap from '../../../hooks/useCleverTap';
import {
  CLEVERTAP_ADDITIONAL,
  CLEVERTAP_SCREEN_NAME,
} from '../../../helpers/ClevertapKeys';
import { useSearchParams } from 'react-router-dom';
import useOnceCallback from '../../../hooks/useOnceCallback';
import { ActionsMenuItems, AddMerchantAndSearchButton } from './uiHelpers';
import { fetchMcProductList } from '../../../services/MySubmissions';

const tableHeight = calcTableHeight();
const tableWidth = calcTableWidth();

const MyMerchantsView = () => {
  const cleverTap = useCleverTap();
  const [tableConfig, setTableConfig] =
    useState<TableSettingsType>(tableConfigDefault);
  const [
    isFetchingCompanyStampCardDraftStatus,
    setIsFetchingCompanyStampCardDrafts,
  ] = useState(false);

  // get query from url
  const [queryString] = useSearchParams();
  const { ...queryStrings } = Object.fromEntries(queryString);
  const query = queryStrings.query?.trim();
  const isSearching = !!query;

  const { data, isFetching, isLoading } = useFetchMerchants({
    currentPage: tableConfig.current,
    limit: tableConfig.pageSize,
    sort_order: tableConfig.sort_order,
    // when query is present, filter by merchant_name_or_bd_email
    // else show logged in user's merchants
    filter: `${isSearching ? `merchant_name_or_bd_email=${query}` : 'assigned_to=my'}|status=approved|${tableConfig.filter || ''}`,
    dataset: 'outlets',
  });

  const isTableLoading =
    isFetching || isLoading || isFetchingCompanyStampCardDraftStatus;

  useOnceCallback(() =>
    cleverTap.screenDisplayed.push({
      screen_name: CLEVERTAP_SCREEN_NAME.MY_MERCHANT,
      platform: CLEVERTAP_ADDITIONAL.MERCHANT_CONNECT,
    }),
  );

  // for fave merchants only
  // check for company stamp card draft status
  const getCompanyStampCardDrafts = async (companyId: number) => {
    setIsFetchingCompanyStampCardDrafts(true);
    const response = await fetchMcProductList({
      queryKey: [
        'mc_products',
        {
          filter: `product_type=LoyaltySetting|mc_my_submission_status=draft|partner=${companyId}`,
        },
      ],
      meta: undefined,
    }).finally(() => setIsFetchingCompanyStampCardDrafts(false));

    return response;
  };

  const columns: ColumnsType<CompanyType> = [
    {
      title: 'Company',
      dataIndex: 'merchant_type',
      filterIcon: FilterIcon,
      filters: [
        { text: `Fave`, value: 'fave' },
        { text: `FastPay`, value: 'fastpay' },
      ],
      width: 130,
      fixed: 'left',
      render: RenderColumn.merchantType,
    },
    {
      title: ({
        sortColumns,
      }: {
        sortColumns: {
          column: ColumnType<{}>;
          order: SortOrder;
        }[];
      }) => (
        <HeaderWithSort sortColumns={sortColumns} title={'Merchant Name'} />
      ),
      dataIndex: 'name',
      width: 210,
      sorter: true,
      filterIcon: FilterIcon,
      render: name => name,
    },
    {
      title: 'Status',
      dataIndex: 'flagged_reason',
      width: 150,
      render: RenderColumn.merchantStatus,
    },
    {
      title: 'Outlets',
      dataIndex: 'outlets',
      width: 90,
      render: (outlets: []) => <>{outlets.length}</>,
    },
    {
      title: 'BD assigned',
      dataIndex: 'bd_manager_email',
      width: 208,
      render: name => valueOrDash(name),
    },
    {
      title: 'Date Created',
      dataIndex: 'created_at',
      width: 208,
      render: date => dateOrDash(date),
    },
    {
      title: 'Master Contract',
      dataIndex: 'contract_status',
      width: 176,
      render: status => {
        const statusColor = (() => {
          if (status === 'Signed') return 'success';
          else if (status === 'Not sent') return 'error';
          else return 'default';
        })();

        return <Tag color={statusColor}>{status}</Tag>;
      },
    },
    {
      title: 'Products',
      dataIndex: 'products',
      width: 140,
      render: RenderColumn.products,
    },
    {
      title: 'Actions',
      key: 'action',
      width: 140,
      fixed: 'right',
      render: (company: CompanyType) => (
        <ActionsMenuItems
          companyData={company}
          getCompanyStampCardDrafts={getCompanyStampCardDrafts}
        />
      ),
    },
  ];

  const handleChangeTable = createOnTableChangeHandler(
    setTableConfig,
    filters =>
      formatFilter(
        isSearching
          ? // no need to filter by merchant_name_or_bd_email when searching using searchbar
            // because already handled in api above
            [{ key: 'merchant_type', value: filters.merchant_type }]
          : [
              { key: 'merchant_type', value: filters.merchant_type },
              // filter by merchant_name_or_bd_email when searching using Merchant Name column
              { key: 'merchant_name_or_bd_email', value: filters.name },
            ],
      ),
  );

  return (
    <>
      <PageHeaderWithSpace
        title={query ? 'Search' : 'My Merchants'}
        subTitle={
          query
            ? "Here is what you're looking for"
            : 'Here is the list of merchants you are working with.'
        }
        additionalContent={<AddMerchantAndSearchButton />}
      />
      <Table
        size={'large'}
        rowKey={row => row.id}
        loading={isTableLoading}
        dataSource={data?.fave_save_merchants}
        columns={columns}
        scroll={{
          x: tableWidth,
          y: tableHeight,
        }}
        className={style.table}
        pagination={{
          total: data?.count,
          position: ['bottomCenter'],
          pageSize: tableConfig.pageSize,
          current: tableConfig.current,
          showSizeChanger: false,
        }}
        locale={{
          emptyText: !isFetching && (
            <Empty
              emptyType={'cant-find-anything'}
              style={{ height: tableHeight }}
            />
          ),
        }}
        onChange={handleChangeTable}
      />
    </>
  );
};

export default MyMerchantsView;
